<template>
  <div class="d-flex align-items-start resource-dropdown">
    <ValidationProvider :vid="vid" :name="$attrs.name" :rules="rules" style="flex: 1">
      <b-form-group slot-scope="{ valid, errors }" v-bind="$attrs">
        <template #label>
          <label>
            {{ $attrs.label }}
            <span v-if="$attrs.required || rules.includes('required')" class="text-danger font-bold"
              >*</span
            >
          </label>
        </template>

        <v-select
          v-model="selectedCustomer"
          :options="customerOptions"
          label="name"
          :filterable="false"
          placeholder="Select Customer"
          :loading="loadingCustomers || loadingSelectedCustomer"
          :clearable="true"
          :class="!!errors[0] ? 'is-invalid' : ''"
          @search="onSearch"
          @open="onDropdownOpen"
          :disabled="$attrs.disabled"
          :state="errors[0] ? false : rules && rules.includes('required') && valid ? true : null"
          v-bind="$attrs"
        >
          <template #no-options>
            <div class="text-center text-muted">No customers found</div>
          </template>

          <template #spinner="{ loading }">
            <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </template>

          <template #selected-option="option">
            <strong>{{ option.name }}</strong>
            <span v-if="option.ntnNumber || option.cnicNumber" class="text-muted ml-50">
              (
              <span v-if="option.ntnNumber">NTN: {{ option.ntnNumber }}</span>
              <span v-if="option.ntnNumber && option.cnicNumber">, </span>
              <span v-if="option.cnicNumber">CNIC: {{ option.cnicNumber }}</span>
              )
            </span>
          </template>

          <template #option="option">
            <div>
              <strong class="d-block">{{ option.name }}</strong>
              <small v-if="option.ntnNumber || option.cnicNumber" class="text-muted">
                <span v-if="option.ntnNumber">NTN ({{ option.ntnNumber }})</span>
                <span v-if="option.ntnNumber && option.cnicNumber">, </span>
                <span v-if="option.cnicNumber">CNIC ({{ option.cnicNumber }})</span>
              </small>
            </div>
          </template>
        </v-select>

        <b-form-invalid-feedback>
          {{ errors[0] ? errors[0].replace('{field}', $attrs.label) : '' }}
        </b-form-invalid-feedback>
      </b-form-group>
    </ValidationProvider>
    <create-customer-modal @submit="onCustomerCreated" />
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback, BButton } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import CustomerStoreModule from '../../customer/CustomerStoreModule';
import CreateCustomerModal from '../../customer/CreateCustomerModal.vue';

export default {
  name: 'CustomerDropdownField',
  components: {
    ValidationProvider,
    vSelect,
    BFormGroup,
    BFormInvalidFeedback,
    BButton,

    // Custom Components
    CreateCustomerModal,
  },
  emits: ['selected'],
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'Customer',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const CUSTOMER_STORE_MODULE_NAME = 'customer';

    // Register module
    if (!store.hasModule(CUSTOMER_STORE_MODULE_NAME))
      store.registerModule(CUSTOMER_STORE_MODULE_NAME, CustomerStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CUSTOMER_STORE_MODULE_NAME))
        store.unregisterModule(CUSTOMER_STORE_MODULE_NAME);
    });

    // Reactive references
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingCustomers = ref(false);
    const loadingSelectedCustomer = ref(false);
    const customerOptions = ref([]);
    const allCustomers = ref([]);
    const selectedCustomer = ref(null);
    const selectedCustomerId = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);
    const userID = computed(() => store.state.user?.userID);

    // Methods
    const matchesCustomerSearch = (customer, term) => {
      const searchableFields = [
        customer.id,
        customer.name,
        customer.code,
        customer.ntnNumber,
        customer.cnicNumber,
        customer.cellNumber,
        customer.whatsappOrUAN,
        customer.whatsappNumber,
      ];

      return searchableFields.some(
        (field) => field && String(field).toLowerCase().includes(term)
      );
    };

    const applyCustomerFilter = () => {
      const term = (searchTerm.value || '').trim().toLowerCase();

      if (!term) {
        customerOptions.value = [...allCustomers.value];
        return;
      }

      customerOptions.value = allCustomers.value.filter((customer) =>
        matchesCustomerSearch(customer, term)
      );
    };

    const ensureCustomerInOptions = (customer) => {
      if (!customer || !customer.id) return;

      const exists = allCustomers.value.some((item) => item.id === customer.id);
      if (!exists) {
        allCustomers.value = [customer, ...allCustomers.value];
      }

      applyCustomerFilter();
    };

    const setSelectedCustomer = (customer) => {
      if (!customer) {
        selectedCustomer.value = null;
        return;
      }

      ensureCustomerInOptions(customer);
      selectedCustomer.value = customer;
    };

    const fetchCustomers = async () => {
      if (!businessID.value) return;

      loadingCustomers.value = true;
      try {
        const response = await api.get(`/CustomersFBR/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: '',
            pageNumber: pageNumber.value,
            pageSize: 999,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          allCustomers.value = [...response.data.data.items];
          applyCustomerFilter();
        } else {
          console.error(
            '=== CustomerDropdownField options Error in paginated customer data format:',
            response.data
          );
          allCustomers.value = [];
          customerOptions.value = [];
        }
      } catch (error) {
        console.error('=== CustomerDropdownField options Error fetching customers:', error);
      } finally {
        loadingCustomers.value = false;
      }
    };

    const fetchCustomer = async (id) => {
      const response = await api.get(
        `/CustomersFBR/LoadSelectedData/${businessID.value}?id=${id}&userID=${userID.value}&id=${id}`
      );

      if (
        response.data &&
        response.data.status === 'success' &&
        response.data.data &&
        response.data.data.entity
      ) {
        return response.data.data.entity;
      } else {
        console.error(
          '=== CustomerDropdownField options Error in paginated customer data format:',
          response.data
        );
        return null;
      }
    };

    const onCustomerCreated = (customerId) => {
      console.log('=== CustomerDropdownField onCustomerCreated:', customerId);
      fetchCustomer(customerId).then((customer) => {
        setSelectedCustomer(customer);
        fetchCustomers();
      });
    };

    const onDropdownOpen = () => {
      if (!businessID.value) return;

      if (!allCustomers.value.length && !loadingCustomers.value) {
        fetchCustomers();
        return;
      }

      applyCustomerFilter();
    };

    const onSearch = (searchTermValue) => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }

      searchTerm.value = searchTermValue || '';

      if (!searchTerm.value.trim()) {
        applyCustomerFilter();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        applyCustomerFilter();
      }, 350);
    };

    // Watchers
    watch(
      selectedCustomer,
      (newVal) => {
        selectedCustomerId.value = newVal?.id;
        emit('input', newVal?.id);
        emit('selected', newVal);
      },
      { immediate: true }
    );

    watch(
      businessID,
      (newVal) => {
        if (newVal) {
          fetchCustomers();
        }
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const customerId = props.value;

      if (customerId) {
        loadingSelectedCustomer.value = true;
        store
          .dispatch('customer/fetchCustomer', { id: customerId })
          .then((customer) => {
            setSelectedCustomer(customer);
          })
          .catch((error) => {
            console.error('error:', error);
          })
          .finally(() => {
            loadingSelectedCustomer.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      searchTerm,
      pageNumber,
      loadingCustomers,
      loadingSelectedCustomer,
      customerOptions,
      allCustomers,
      selectedCustomer,
      selectedCustomerId,
      searchTimeout,

      // Computed properties
      businessID,
      userID,

      // Methods
      fetchCustomers,
      fetchCustomer,
      applyCustomerFilter,
      matchesCustomerSearch,
      ensureCustomerInOptions,
      setSelectedCustomer,
      onDropdownOpen,
      onSearch,
      onCustomerCreated,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
